import { EntityType, formatDateTimeWithLocale, isSharedIncoming, useAppSelector } from '@frontend/common';
import { DeceasedStatusBadge } from '@frontend/deceased/badge';
import { IDeceased } from '@frontend/deceased/types';
import { EventLevel } from '@frontend/events/types';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { awaitResolve, getResolvedEntity, resolveStore } from '@frontend/repository';
import { TransactionNivalizExtendedClient } from '@frontend/transaction/api';
import {
    HistoryEventDetails,
    TransactionHistory,
    TransactionStateBadge,
    getHistoryEventTitleClassName,
    mapEventDataToDisplay
} from '@frontend/transaction/common';
import { ExtendedNivalizTransaction } from '@frontend/transaction/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformEventReferenceBadges } from '../../modal/platform-event-reference-badges/platform-event-reference-badges.component';
import { usePlatformModalHost } from '../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { createPlatformObjectBadgeUtils } from '../../modal/platform-object-badge/platform-object-badge.utils';

export function pickCoffinAndUrnTransactions(transactions: ExtendedNivalizTransaction[]): {
    coffin: ExtendedNivalizTransaction | null;
    urn: ExtendedNivalizTransaction | null;
} {
    return {
        coffin: transactions.find((transaction) => transaction.feature === Feature.INBOUND_TRANSACTION) ?? null,
        urn: transactions.find((transaction) => transaction.feature === Feature.OUTBOUND_TRANSACTION) ?? null
    };
}

export const DeceasedDetailsCard = ({ deceased }: { deceased: IDeceased }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={deceased}
            cols={2}
            order={['name', 'date_of_death', 'state', 'elys_number', 'spot_id', 'cremation_timestamp']}
            keyOverwrite={
                new Map([
                    ['name', CommonMessage.FORMS.NAME],
                    ['date_of_death', CommonMessage.OBJECTS.DECEASED.DATE_OF_DEATH],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                    ['elys_number', CommonMessage.OBJECTS.DECEASED.ELYS_NUMBER],
                    ['spot_id', CommonMessage.OBJECTS.SPOT.DEFAULT],
                    ['cremation_timestamp', CommonMessage.OBJECTS.DECEASED.CREMATION]
                ])
            }
            valueOverwrite={
                new Map<string, (obj: IDeceased) => React.ReactNode>([
                    ['state', (obj) => <DeceasedStatusBadge status={obj.state} />],
                    [
                        'spot_id',
                        (obj) =>
                            obj.spot_id ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.SPOT}
                                    id={obj.spot_id}
                                />
                            ) : (
                                '/'
                            )
                    ]
                ])
            }
        />
    </div>
);

const RelatedTransactionCard = ({
    title,
    emptyLabel,
    transaction
}: {
    title: React.ReactNode;
    emptyLabel: React.ReactNode;
    transaction: ExtendedNivalizTransaction | null;
}) => {
    const { openPlatformDetail } = usePlatformModalHost();
    const { locale } = useLocale();
    const transactionId = transaction?.id ?? '';
    const isForeign = Boolean(transaction && isSharedIncoming(transaction.shared));
    const { resolve } = createPlatformObjectBadgeUtils(transactionId, EntityType.TRANSACTION, locale, undefined);
    const resolveState = useAppSelector(useSelector, resolveStore);
    const requestedIdRef = useRef<string | undefined>(undefined);
    const [settledId, setSettledId] = useState<string | undefined>(undefined);

    const entity = useMemo(() => {
        if (!transactionId || !isForeign) {
            return null;
        }
        return getResolvedEntity<{ id: string }>(resolveState, EntityType.TRANSACTION, transactionId);
    }, [transactionId, isForeign, resolveState]);

    const resolveSettled = !isForeign || !transactionId || settledId === transactionId || Boolean(entity);

    useEffect(() => {
        if (!isForeign || !transactionId || entity || requestedIdRef.current === transactionId) {
            return;
        }
        requestedIdRef.current = transactionId;
        void awaitResolve(transactionId, resolve).then(() => {
            if (requestedIdRef.current === transactionId) {
                setSettledId(transactionId);
            }
        });
    }, [isForeign, transactionId, entity, resolve]);

    const isForeignResolving = isForeign && !entity && !resolveSettled;
    const isDisabled = isForeign && resolveSettled && !entity;
    const isClickable = Boolean(transaction) && !isForeignResolving && !isDisabled;

    if (!transaction) {
        return (
            <div className='bg-background-surface outline-border-default flex flex-1 flex-col gap-2 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default text-base font-semibold'>{title}</div>
                <div className='text-brand-muted text-sm'>{emptyLabel}</div>
            </div>
        );
    }

    const cardBody = (
        <>
            <div className='text-foreground-default text-base font-semibold'>{title}</div>
            <div className={`text-sm font-medium ${isDisabled ? 'text-brand-muted' : 'text-foreground-default'}`}>
                {transaction.tracking_number || transaction.id}
            </div>
            <TransactionStateBadge state={transaction.state} />
        </>
    );

    const baseClassName = `bg-background-surface outline-border-default flex flex-1 flex-col items-start gap-2 rounded-xl p-4 text-left outline-1 outline-offset-[-1px]${
        isForeignResolving ? ' animate-pulse' : ''
    }`;

    if (isClickable) {
        return (
            <button
                type='button'
                data-testid={`deceased-related-transaction-${transaction.id}`}
                className={`${baseClassName} hover:bg-brand-primary/5 cursor-pointer`}
                onClick={() =>
                    openPlatformDetail({
                        entityType: EntityType.TRANSACTION,
                        id: transaction.id,
                        context: { application: Application.NIVALIZ }
                    })
                }>
                {cardBody}
            </button>
        );
    }

    return (
        <div
            data-testid={`deceased-related-transaction-${transaction.id}`}
            className={`${baseClassName} cursor-default`}>
            {cardBody}
        </div>
    );
};

export const DeceasedRelatedTransactionsSection = ({ deceased }: { deceased: IDeceased }) => {
    const [transactions, setTransactions] = useState<ExtendedNivalizTransaction[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
            deceased_id: deceased.id,
            application: Application.NIVALIZ,
            size: '2'
        })
            .then((response) => {
                if (!cancelled) {
                    setTransactions(response.results ?? []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setTransactions([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [deceased.id]);

    const { coffin, urn } = pickCoffinAndUrnTransactions(transactions);

    return (
        <div className='inline-flex w-full flex-col items-start justify-start gap-4 self-stretch'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.PLURAL}</div>
            {loading ? (
                <div className='text-brand-muted text-sm'>
                    <FormattedMessage
                        id='platform.deceased.modal.deceased-detail-sections.loading-transactions'
                        description='Loading label while related Nivaliz transactions are fetched on deceased detail'
                        defaultMessage='Loading transactions…'
                    />
                </div>
            ) : (
                <div className='flex w-full flex-col gap-3 md:flex-row'>
                    <RelatedTransactionCard
                        title={
                            <FormattedMessage
                                id='feature.nivaliz.transactions.type.coffin'
                                description='Label for Nivaliz coffin (inbound) transaction type'
                                defaultMessage='Coffin'
                            />
                        }
                        emptyLabel={
                            <FormattedMessage
                                id='platform.deceased.modal.deceased-detail-sections.no-coffin'
                                description='Empty state when no coffin (inbound) transaction is linked to the deceased'
                                defaultMessage='No coffin transaction'
                            />
                        }
                        transaction={coffin}
                    />
                    <RelatedTransactionCard
                        title={
                            <FormattedMessage
                                id='feature.nivaliz.transactions.type.urn'
                                description='Label for Nivaliz urn (outbound) transaction type'
                                defaultMessage='Urn'
                            />
                        }
                        emptyLabel={
                            <FormattedMessage
                                id='platform.deceased.modal.deceased-detail-sections.no-urn'
                                description='Empty state when no urn (outbound) transaction is linked to the deceased'
                                defaultMessage='No urn transaction'
                            />
                        }
                        transaction={urn}
                    />
                </div>
            )}
        </div>
    );
};

export const DeceasedEventsSection = ({ deceased }: { deceased: IDeceased }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.EVENT_LOGS}</div>
            <TransactionHistory
                key='events'
                entityType={EntityType.DECEASED}
                entityId={deceased.id}
                showChildren
                queryParams={{ level: [EventLevel.INFO, EventLevel.ERROR] }}
                content={(event) => (
                    <div className='flex flex-col gap-2'>
                        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                        <HistoryEventDetails
                            event={event}
                            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
                        />
                        <PlatformEventReferenceBadges event={event} />
                    </div>
                )}
            />
        </div>
    );
};
